import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { adminApi } from "@/lib/api";
import { formatApiError } from "@/admin/AdminAuthContext";
import { PageHeader, LoadingRow, Drawer, Field, Checkbox } from "@/admin/pages/AdminSubsidiaries";

const EMPTY = { slug: "", name: "", logo_path: "", website_url: "", display_order: 100, active: true };

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/clients");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    try {
      if (!editing.id) {
        await adminApi.post("/admin/clients", editing);
        toast.success("Client added.");
      } else {
        const { id, ...body } = editing;
        await adminApi.patch(`/admin/clients/${id}`, body);
        toast.success("Updated.");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try {
      await adminApi.delete(`/admin/clients/${id}`);
      toast.success("Deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-clients-page">
      <PageHeader title="Clients" onAdd={() => setEditing({ ...EMPTY })} testId="add-client" />
      {loading ? <LoadingRow /> : (
        <div className="bg-white border border-softgray divide-y divide-softgray">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-4 p-4 hover:bg-offwhite/70" data-testid={`client-row-${c.slug}`}>
              <div className="w-16 h-12 flex items-center justify-center border border-softgray bg-white shrink-0">
                {c.logo_path
                  ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.logo_path}`} alt="" className="max-h-8 max-w-12 object-contain" />
                  : <span className="text-mutedink text-[10px]">—</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-navy truncate">{c.name}</div>
                <div className="text-mutedink text-xs mt-0.5">slug: {c.slug} · order {c.display_order} · {c.active ? "active" : "hidden"}</div>
              </div>
              <button className="btn-outline-gold !py-1.5 !text-[10px]" onClick={() => setEditing(c)}>Edit</button>
              <button className="btn-outline-gold !py-1.5 !text-[10px] !border-red-300 !text-red-700" onClick={() => remove(c.id)}><Trash2 size={12} /></button>
            </div>
          ))}
          {items.length === 0 && <div className="p-6 text-mutedink text-sm text-center">No clients yet.</div>}
        </div>
      )}
      {editing && (
        <Drawer title={editing.id ? "Edit client" : "New client"}
                onClose={() => setEditing(null)} onSave={save} testId="client-drawer">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Slug" required value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
            <Field label="Name" required value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            <Field label="Logo path (/api/media/…)" value={editing.logo_path}
                   onChange={(v) => setEditing({ ...editing, logo_path: v })} className="sm:col-span-2" />
            <Field label="Website URL" value={editing.website_url}
                   onChange={(v) => setEditing({ ...editing, website_url: v })} className="sm:col-span-2" />
            <Field label="Display order" type="number" value={editing.display_order}
                   onChange={(v) => setEditing({ ...editing, display_order: parseInt(v || "0", 10) })} />
            <Checkbox label="Active" checked={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
          </div>
        </Drawer>
      )}
    </div>
  );
}
